// Skin editor DOCUMENT model: a stack of paint layers composited into one 64x64 skin.
//
// Pure — no React, no DOM — so it runs in `scripts/skin-editor.selftest.ts` under plain Node.
//
// IMMUTABILITY CONTRACT: a `SkinLayer.pixels` buffer that is part of a document is never written
// to again. Every edit builds a new buffer for the ONE layer it touches and a new document object;
// untouched layers are shared by reference. That is what lets the undo history (history.ts) keep
// whole-document snapshots while only paying ~16 KB per pixel edit.

import type { Point } from './types';
import { SKIN_REGIONS, getFaceRegions, getMirrorPixel, type SkinModel } from './skinMap';
// The client-safe half of the community skin layout (no Node APIs): the model's used-pixel mask.
import { usedMask } from '../community/skinLayout';

export const SKIN_SIZE = 64;
export const SKIN_BYTES = SKIN_SIZE * SKIN_SIZE * 4; // 16,384
export const MAX_LAYERS = 16;
export const MAX_LAYER_NAME = 40;

export interface SkinLayer {
  id: string;
  name: string;
  visible: boolean;
  /** 0..1, applied on top of each pixel's own alpha. */
  opacity: number;
  /** A locked layer refuses every pixel edit (and merge-down onto it). */
  locked: boolean;
  /** 64x64 RGBA, row-major, NOT premultiplied. Never mutated once in a document. */
  pixels: Uint8ClampedArray;
}

export interface SkinDocument {
  /** Bottom-most first — index 0 is drawn first. */
  layers: SkinLayer[];
  activeLayerId: string;
  /** Next number for a generated id/name — deterministic, so ids survive autosave and SSR. */
  nextLayerNum: number;
}

export function createBlankPixels(): Uint8ClampedArray {
  return new Uint8ClampedArray(SKIN_BYTES);
}

function makeLayer(doc: Pick<SkinDocument, 'nextLayerNum'>, name: string | undefined, pixels?: Uint8ClampedArray): SkinLayer {
  const n = doc.nextLayerNum;
  return {
    id: `L${n}`,
    name: cleanName(name) || `Layer ${n}`,
    visible: true,
    opacity: 1,
    locked: false,
    pixels: pixels ? new Uint8ClampedArray(pixels) : createBlankPixels(),
  };
}

function cleanName(name: string | undefined): string {
  return (name ?? '').replace(/\s+/g, ' ').trim().slice(0, MAX_LAYER_NAME);
}

/** A fresh document: one fully transparent layer. */
export function createBlankDocument(): SkinDocument {
  const layer = makeLayer({ nextLayerNum: 1 }, 'Layer 1');
  return { layers: [layer], activeLayerId: layer.id, nextLayerNum: 2 };
}

/** A fresh document whose single layer holds `pixels` (an imported skin). */
export function createDocumentFromPixels(pixels: Uint8ClampedArray, name = 'Skin'): SkinDocument {
  if (pixels.length !== SKIN_BYTES) throw new Error(`expected ${SKIN_BYTES} bytes, got ${pixels.length}`);
  const layer = makeLayer({ nextLayerNum: 1 }, name, pixels);
  return { layers: [layer], activeLayerId: layer.id, nextLayerNum: 2 };
}

export function getActiveLayer(doc: SkinDocument): SkinLayer {
  return doc.layers.find(l => l.id === doc.activeLayerId) ?? doc.layers[doc.layers.length - 1];
}

function indexOf(doc: SkinDocument, id: string): number {
  return doc.layers.findIndex(l => l.id === id);
}

// ---------------------------------------------------------------------------------------------
// Compositing
// ---------------------------------------------------------------------------------------------

/**
 * Flatten layers bottom-to-top with normal "source-over" alpha blending. Hidden layers are skipped;
 * each layer's `opacity` scales its pixels' alpha. Output is straight (non-premultiplied) RGBA;
 * fully transparent results are written as 0,0,0,0.
 *
 * A single visible layer at opacity 1 composites to an exact copy of its pixels.
 */
export function compositeLayers(layers: readonly SkinLayer[]): Uint8ClampedArray {
  const out = new Uint8ClampedArray(SKIN_BYTES);
  const active = layers.filter(l => l.visible && l.opacity > 0);
  if (active.length === 0) return out;
  for (let i = 0; i < SKIN_BYTES; i += 4) {
    let r = 0, g = 0, b = 0, a = 0; // a in 0..1
    for (const layer of active) {
      const p = layer.pixels;
      const sa = (p[i + 3] / 255) * layer.opacity;
      if (sa <= 0) continue;
      if (sa >= 1) { r = p[i]; g = p[i + 1]; b = p[i + 2]; a = 1; continue; }
      const keep = a * (1 - sa);
      const oa = sa + keep;
      r = (p[i] * sa + r * keep) / oa;
      g = (p[i + 1] * sa + g * keep) / oa;
      b = (p[i + 2] * sa + b * keep) / oa;
      a = oa;
    }
    if (a > 0) {
      out[i] = Math.round(r);
      out[i + 1] = Math.round(g);
      out[i + 2] = Math.round(b);
      out[i + 3] = Math.round(a * 255);
    }
  }
  return out;
}

export function compositeDocument(doc: SkinDocument): Uint8ClampedArray {
  return compositeLayers(doc.layers);
}

/** True when no layer has a single non-transparent pixel (hidden layers included). */
export function isBlankDocument(doc: SkinDocument): boolean {
  for (const layer of doc.layers) {
    const p = layer.pixels;
    for (let i = 3; i < p.length; i += 4) if (p[i] !== 0) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------------------------
// Layer operations. Each returns a NEW document, or the SAME object when nothing changed (callers
// use reference equality to skip a history entry).
// ---------------------------------------------------------------------------------------------

export function setActiveLayer(doc: SkinDocument, id: string): SkinDocument {
  if (doc.activeLayerId === id || indexOf(doc, id) < 0) return doc;
  return { ...doc, activeLayerId: id };
}

/**
 * Insert a layer directly ABOVE the active layer and make it active. This is the entry point for
 * "import a part as a new layer": pass the part's 64x64 pixels (transparent everywhere else).
 * Returns the same doc when the stack is full (MAX_LAYERS).
 */
export function addLayer(doc: SkinDocument, opts: { name?: string; pixels?: Uint8ClampedArray } = {}): SkinDocument {
  if (doc.layers.length >= MAX_LAYERS) return doc;
  if (opts.pixels && opts.pixels.length !== SKIN_BYTES) throw new Error(`expected ${SKIN_BYTES} bytes`);
  const layer = makeLayer(doc, opts.name, opts.pixels);
  const at = indexOf(doc, doc.activeLayerId);
  const layers = doc.layers.slice();
  layers.splice(at < 0 ? layers.length : at + 1, 0, layer);
  return { layers, activeLayerId: layer.id, nextLayerNum: doc.nextLayerNum + 1 };
}

export function deleteLayer(doc: SkinDocument, id: string): SkinDocument {
  const i = indexOf(doc, id);
  if (i < 0 || doc.layers.length <= 1) return doc;
  const layers = doc.layers.filter(l => l.id !== id);
  const activeLayerId = doc.activeLayerId === id ? layers[Math.max(0, i - 1)].id : doc.activeLayerId;
  return { ...doc, layers, activeLayerId };
}

export function duplicateLayer(doc: SkinDocument, id: string): SkinDocument {
  const i = indexOf(doc, id);
  if (i < 0 || doc.layers.length >= MAX_LAYERS) return doc;
  const src = doc.layers[i];
  const copy: SkinLayer = {
    ...src,
    id: `L${doc.nextLayerNum}`,
    name: cleanName(`${src.name} copy`),
    locked: false,
    pixels: src.pixels, // shared: buffers are immutable, the first edit replaces it
  };
  const layers = doc.layers.slice();
  layers.splice(i + 1, 0, copy);
  return { layers, activeLayerId: copy.id, nextLayerNum: doc.nextLayerNum + 1 };
}

/** dir +1 = up the stack (drawn later / on top), -1 = down. */
export function moveLayer(doc: SkinDocument, id: string, dir: 1 | -1): SkinDocument {
  const i = indexOf(doc, id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= doc.layers.length) return doc;
  const layers = doc.layers.slice();
  [layers[i], layers[j]] = [layers[j], layers[i]];
  return { ...doc, layers };
}

export type LayerProps = Partial<Pick<SkinLayer, 'name' | 'visible' | 'opacity' | 'locked'>>;

export function updateLayerProps(doc: SkinDocument, id: string, props: LayerProps): SkinDocument {
  const i = indexOf(doc, id);
  if (i < 0) return doc;
  const cur = doc.layers[i];
  const next: SkinLayer = { ...cur };
  if (props.name !== undefined) {
    const n = cleanName(props.name);
    if (n) next.name = n;
  }
  if (props.visible !== undefined) next.visible = props.visible;
  if (props.locked !== undefined) next.locked = props.locked;
  if (props.opacity !== undefined && Number.isFinite(props.opacity)) {
    next.opacity = Math.round(Math.min(1, Math.max(0, props.opacity)) * 100) / 100;
  }
  if (next.name === cur.name && next.visible === cur.visible && next.locked === cur.locked && next.opacity === cur.opacity) {
    return doc;
  }
  const layers = doc.layers.slice();
  layers[i] = next;
  return { ...doc, layers };
}

/** Replace one layer's pixels (the buffer is adopted — the caller must not write to it again). */
export function setLayerPixels(doc: SkinDocument, id: string, pixels: Uint8ClampedArray): SkinDocument {
  const i = indexOf(doc, id);
  if (i < 0 || doc.layers[i].pixels === pixels) return doc;
  if (pixels.length !== SKIN_BYTES) throw new Error(`expected ${SKIN_BYTES} bytes`);
  const layers = doc.layers.slice();
  layers[i] = { ...layers[i], pixels };
  return { ...doc, layers };
}

/** Why Merge down is not possible for layer `id` right now, or null when it is. (The Layers panel's hint.) */
export function mergeDownProblem(doc: SkinDocument, id: string): string | null {
  const i = indexOf(doc, id);
  if (i <= 0) return 'There is no layer below';
  const upper = doc.layers[i];
  const lower = doc.layers[i - 1];
  if (lower.locked) return 'The layer below is locked';
  if (!upper.visible || !lower.visible) return 'Show both layers to merge them';
  return null;
}

/**
 * Merge `id` into the layer below it. The result looks identical to the two layers drawn separately:
 * both opacities are baked into the pixels and the merged layer has opacity 1. The merged layer keeps
 * the lower layer's id and name and becomes active.
 *
 * REFUSED (same doc back) when the lower layer is locked, or when EITHER layer is hidden: compositing
 * skips a hidden layer, so merging used to DELETE a hidden lower layer's pixels (or a hidden upper
 * layer's) without a word. Showing both first makes the result the thing you see.
 */
export function mergeDown(doc: SkinDocument, id: string): SkinDocument {
  if (mergeDownProblem(doc, id)) return doc;
  const i = indexOf(doc, id);
  const upper = doc.layers[i];
  const lower = doc.layers[i - 1];
  const merged: SkinLayer = {
    ...lower,
    visible: true,
    opacity: 1,
    pixels: compositeLayers([lower, upper]),
  };
  const layers = doc.layers.slice();
  layers.splice(i - 1, 2, merged);
  return { ...doc, layers, activeLayerId: merged.id };
}

/** Collapse the whole stack into one layer holding exactly the exported skin. */
export function flattenDocument(doc: SkinDocument): SkinDocument {
  if (doc.layers.length === 1) {
    const only = doc.layers[0];
    if (only.visible && only.opacity === 1) return doc;
  }
  const bottom = doc.layers[0];
  const flat: SkinLayer = {
    id: bottom.id,
    name: doc.layers.length === 1 ? bottom.name : 'Flattened',
    visible: true,
    opacity: 1,
    locked: false,
    pixels: compositeDocument(doc),
  };
  return { ...doc, layers: [flat], activeLayerId: flat.id };
}

// ---------------------------------------------------------------------------------------------
// Display-only helpers
// ---------------------------------------------------------------------------------------------

/**
 * The rectangles Minecraft forces OPAQUE when it loads a skin (vanilla `setNoAlpha(0,0,32,16)`,
 * `(0,16,64,32)`, `(16,48,48,64)`): every BASE box of both arm models. The stored RGB is kept and the
 * alpha is thrown away — so a base pixel stored as 0,0,0,0 is drawn BLACK in game, and a half
 * transparent one is drawn solid.
 */
export const GAME_OPAQUE_RECTS: readonly { x0: number; y0: number; x1: number; y1: number }[] = [
  { x0: 0, y0: 0, x1: 32, y1: 16 },
  { x0: 0, y0: 16, x1: 64, y1: 32 },
  { x0: 16, y0: 48, x1: 48, y1: 64 },
];

/**
 * The texture the 3D preview draws: the composite exactly as the GAME will draw it — the base
 * rectangles forced opaque with their stored RGB, so an unpainted or erased base pixel is black on the
 * model, as it will be in Minecraft. (It used to be blended over a gray checkerboard, which showed a
 * body the game never draws.) The 2D canvases keep their checkerboard as a "nothing painted here" cue,
 * and `translucentBaseWarning` says so in words. Display only: never exported, never saved.
 */
export function compositeForPreview(composite: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(composite);
  for (const r of GAME_OPAQUE_RECTS) {
    for (let y = r.y0; y < r.y1; y++) {
      for (let x = r.x0; x < r.x1; x++) out[(y * SKIN_SIZE + x) * 4 + 3] = 255;
    }
  }
  return out;
}

/**
 * The skin EXACTLY as publishing stores it — the client mirror of the server's `normaliseSkinPixels`
 * (app/lib/community/types/skin.server.ts, which needs Node and cannot be bundled): pixels this model
 * never draws become 0,0,0,0, the color of an alpha-0 pixel is cleared, and every BASE-rectangle pixel is
 * made opaque with its stored RGB (so an empty base pixel is opaque BLACK, a half-transparent one solid).
 * The publish dialog previews THIS, so what the author sees is what the moderator and the game see.
 * `scripts/skin-editor.selftest.ts` compares it byte-for-byte with the server function.
 */
export function compositeAsPublished(composite: Uint8ClampedArray, model: SkinModel): Uint8ClampedArray {
  const mask = usedMask(model);
  const out = new Uint8ClampedArray(composite);
  for (let i = 0; i < SKIN_SIZE * SKIN_SIZE; i++) {
    if (!mask[i]) { out.fill(0, i * 4, i * 4 + 4); continue; }
    if (out[i * 4 + 3] === 0) out.fill(0, i * 4, i * 4 + 3);
    const x = i % SKIN_SIZE, y = Math.floor(i / SKIN_SIZE);
    if (GAME_OPAQUE_RECTS.some(r => x >= r.x0 && x < r.x1 && y >= r.y0 && y < r.y1)) out[i * 4 + 3] = 255;
  }
  return out;
}

/**
 * How many pixels of this model's BASE faces are not fully opaque (alpha < 255) in `composite`. The game
 * (and publishing) draws each of them solid: an empty one black, a semi-transparent one at full strength.
 */
export function translucentBasePixelCount(composite: Uint8ClampedArray, model: SkinModel): number {
  let n = 0;
  for (const f of getFaceRegions(model)) {
    if (f.layer !== 'base') continue;
    for (let y = f.y; y < f.y + f.height; y++) {
      for (let x = f.x; x < f.x + f.width; x++) if (composite[(y * SKIN_SIZE + x) * 4 + 3] < 255) n++;
    }
  }
  return n;
}

/**
 * The non-blocking heads-up for base pixels that are not fully opaque, or null when there are none — or
 * when nothing at all is painted yet (a blank start is not worth a warning).
 */
export function translucentBaseWarning(composite: Uint8ClampedArray, model: SkinModel): string | null {
  const n = translucentBasePixelCount(composite, model);
  if (n === 0) return null;
  let painted = false;
  for (let i = 3; i < composite.length; i += 4) if (composite[i] !== 0) { painted = true; break; }
  if (!painted) return null;
  return n === 1
    ? '1 inner-layer pixel isn’t fully opaque — it’ll show solid/black in game.'
    : `${n} inner-layer pixels aren’t fully opaque — they’ll show solid/black in game.`;
}

/**
 * The Base / Overlay / Final view filter for the 2D canvases (display only): in 'base' mode the
 * overlay faces are faded, in 'overlay' mode the base faces are, 'both' shows everything as is.
 * It filters UV REGIONS of the sheet, not paint layers.
 */
export function applyViewFilter(composite: Uint8ClampedArray, mode: 'base' | 'overlay' | 'both', model: SkinModel, fade = 0.3): Uint8ClampedArray {
  if (mode === 'both') return composite;
  const out = new Uint8ClampedArray(composite);
  for (const f of getFaceRegions(model)) {
    if (f.layer === mode) continue;
    for (let y = f.y; y < f.y + f.height; y++) {
      for (let x = f.x; x < f.x + f.width; x++) {
        const i = (y * SKIN_SIZE + x) * 4 + 3;
        out[i] = Math.round(out[i] * fade);
      }
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Legacy: the gray checkerboard the old editor baked into every blank BASE pixel
// ---------------------------------------------------------------------------------------------

const LEGACY_LIGHT = 200; // #c8c8c8 where (x + y) is even
const LEGACY_DARK = 184;  // #b8b8b8 where (x + y) is odd

function isLegacyCheckerPixel(p: Uint8ClampedArray, x: number, y: number): boolean {
  const i = (y * SKIN_SIZE + x) * 4;
  const v = (x + y) % 2 === 0 ? LEGACY_LIGHT : LEGACY_DARK;
  return p[i + 3] === 255 && p[i] === v && p[i + 1] === v && p[i + 2] === v;
}

/**
 * Skins made with the old editor (saved to an account, downloaded, even uploaded to minecraft.net)
 * carry its opaque #c8c8c8/#b8b8b8 checkerboard in every unpainted BASE pixel. On import, remove it —
 * but only where it is unmistakably that pattern: per classic base face, and only if the face holds
 * at least one horizontally ADJACENT light/dark pair in the exact phase the old editor used. A flat
 * #c8c8c8 or #b8b8b8 area painted on purpose has no such pair and is left alone.
 *
 * Returns a NEW buffer (or the same one when nothing matched) and how many pixels were cleared.
 */
export function stripLegacyCheckerboard(pixels: Uint8ClampedArray): { pixels: Uint8ClampedArray; cleared: number } {
  let out: Uint8ClampedArray | null = null;
  let cleared = 0;
  for (const r of SKIN_REGIONS) {
    if (r.layer !== 'base') continue;
    let hasPair = false;
    for (let y = r.y; y < r.y + r.height && !hasPair; y++) {
      for (let x = r.x; x + 1 < r.x + r.width; x++) {
        if (isLegacyCheckerPixel(pixels, x, y) && isLegacyCheckerPixel(pixels, x + 1, y)) { hasPair = true; break; }
      }
    }
    if (!hasPair) continue;
    for (let y = r.y; y < r.y + r.height; y++) {
      for (let x = r.x; x < r.x + r.width; x++) {
        if (!isLegacyCheckerPixel(pixels, x, y)) continue;
        if (!out) out = new Uint8ClampedArray(pixels);
        out.fill(0, (y * SKIN_SIZE + x) * 4, (y * SKIN_SIZE + x) * 4 + 4);
        cleared++;
      }
    }
  }
  return { pixels: out ?? pixels, cleared };
}

/**
 * A legacy 64x32 skin (drawn into the top half of a 64x64 buffer) has no left arm or left leg. The
 * game builds them from the right limbs FACE BY FACE: each face is copied mirrored, and the right
 * limb's outer side becomes the left limb's outer side. That is exactly `getMirrorPixel`, so reuse
 * it. Returns a new buffer; only the base faces of the left arm/leg are written.
 */
export function expandLegacySkin(pixels: Uint8ClampedArray): Uint8ClampedArray {
  const out = new Uint8ClampedArray(pixels);
  for (const f of getFaceRegions('classic')) {
    if (f.layer !== 'base' || (f.part !== 'rightArm' && f.part !== 'rightLeg')) continue;
    for (let y = f.y; y < f.y + f.height; y++) {
      for (let x = f.x; x < f.x + f.width; x++) {
        const m = getMirrorPixel(x, y, 'classic');
        if (!m) continue;
        const s = (y * SKIN_SIZE + x) * 4;
        const d = (m.y * SKIN_SIZE + m.x) * 4;
        out[d] = pixels[s]; out[d + 1] = pixels[s + 1]; out[d + 2] = pixels[s + 2]; out[d + 3] = pixels[s + 3];
      }
    }
  }
  return out;
}

/**
 * Vanilla's `doNotchTransparencyHack(32, 0, 64, 32)` for LEGACY 64x32 skins: if the hat area has no
 * pixel with alpha below 128 (a skin drawn without transparency), the game draws no hat at all. The
 * check covers x 32–63, y 0–31 as in vanilla; the clearing that survives there is the hat rows (y 0–15)
 * — vanilla clears 0–31 too, but `setNoAlpha(0,16,64,32)` then makes rows 16–31 opaque again.
 * Only for 64x32 inputs; returns a NEW buffer, or the same one when the rule does not apply.
 */
export function notchTransparencyHack(pixels: Uint8ClampedArray): Uint8ClampedArray {
  for (let y = 0; y < 32; y++) {
    for (let x = 32; x < 64; x++) if (pixels[(y * SKIN_SIZE + x) * 4 + 3] < 128) return pixels;
  }
  const out = new Uint8ClampedArray(pixels);
  for (let y = 0; y < 16; y++) out.fill(0, (y * SKIN_SIZE + 32) * 4, (y * SKIN_SIZE + 64) * 4);
  return out;
}

/** Convenience for the importer: strip the legacy checkerboard and build a one-layer document. */
export function documentFromImportedSkin(pixels: Uint8ClampedArray, name = 'Skin'): SkinDocument {
  return createDocumentFromPixels(stripLegacyCheckerboard(pixels).pixels, name);
}

/** Every pixel coordinate of the 64x64 sheet whose alpha is non-zero in `pixels`. Test helper. */
export function opaquePoints(pixels: Uint8ClampedArray): Point[] {
  const pts: Point[] = [];
  for (let y = 0; y < SKIN_SIZE; y++) {
    for (let x = 0; x < SKIN_SIZE; x++) if (pixels[(y * SKIN_SIZE + x) * 4 + 3] !== 0) pts.push({ x, y });
  }
  return pts;
}
